import React, { useState } from 'react';
import {
  LayoutDashboard,
  Plus,
  Sliders,
  Filter,
  Calendar,
  Layers,
  X,
  Trash2,
  RefreshCw,
  FolderPlus,
} from 'lucide-react';
import { CustomAnalysisDashboard, AnalysisQueryFilter } from '../types';

interface AnalysisFilterToolbarProps {
  dashboards: CustomAnalysisDashboard[];
  selectedDashboardId: string;
  onSelectDashboard: (id: string) => void;
  onCreateDashboard: (title: string, description?: string) => void;
  onDeleteDashboard: (id: string) => void;
  onOpenAddWidget: () => void;
  isEditMode: boolean;
  onToggleEditMode: () => void;
  filters: AnalysisQueryFilter;
  onFilterChange: (newFilters: AnalysisQueryFilter) => void;
  onClearCrossFilters: () => void;
  onRefresh: () => void;
  loading: boolean;
}

export const AnalysisFilterToolbar: React.FC<AnalysisFilterToolbarProps> = ({
  dashboards,
  selectedDashboardId,
  onSelectDashboard,
  onCreateDashboard,
  onDeleteDashboard,
  onOpenAddWidget,
  isEditMode,
  onToggleEditMode,
  filters,
  onFilterChange,
  onClearCrossFilters,
  onRefresh,
  loading,
}) => {
  const [isNewDashModalOpen, setIsNewDashModalOpen] = useState(false);
  const [newDashTitle, setNewDashTitle] = useState('');
  const [newDashDesc, setNewDashDesc] = useState('');

  const currentDashboard = dashboards.find((d) => d.id === selectedDashboardId);

  const handleCreateDashboardSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDashTitle.trim()) return;
    onCreateDashboard(newDashTitle.trim(), newDashDesc.trim() || undefined);
    setNewDashTitle('');
    setNewDashDesc('');
    setIsNewDashModalOpen(false);
  };

  const hasCrossFilters = filters.crossFilters && Object.keys(filters.crossFilters).length > 0;
  const isGlobalFiltered =
    Boolean(filters.severity && filters.severity !== 'ALL') ||
    Boolean(filters.department && filters.department !== 'ALL') ||
    Boolean(filters.dateRange && filters.dateRange !== 'all') ||
    hasCrossFilters;

  const removeCrossFilter = (dim: string) => {
    if (!filters.crossFilters) return;
    const next = { ...filters.crossFilters };
    delete next[dim];
    onFilterChange({ ...filters, crossFilters: Object.keys(next).length > 0 ? next : undefined });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
      {/* Top Bar: Dashboard Selector, New Buttons & Edit Mode */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          background: '#ffffff',
          padding: '14px 18px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)',
        }}
      >
        {/* Left: Dashboard Switcher */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                padding: '8px',
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LayoutDashboard size={20} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Analysis Dashboard
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <select
                  value={selectedDashboardId}
                  onChange={(e) => onSelectDashboard(e.target.value)}
                  style={{
                    fontWeight: 700,
                    color: '#0f172a',
                    fontSize: '14px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    paddingRight: '16px',
                    outline: 'none',
                  }}
                >
                  {dashboards.map((dash) => (
                    <option key={dash.id} value={dash.id}>
                      {dash.title} {dash.isDefault ? '(Default Template)' : ''}
                    </option>
                  ))}
                </select>
                {currentDashboard && !currentDashboard.isDefault && (
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        confirm(
                          `Are you sure you want to delete dashboard "${currentDashboard.title}" and its widgets?`
                        )
                      ) {
                        onDeleteDashboard(currentDashboard.id);
                      }
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      padding: '2px',
                    }}
                    title="Delete Dashboard"
                  >
                    <Trash2 size={14} color="#dc2626" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setIsNewDashModalOpen(true)}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#0369a1',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #bae6fd',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'background-color 0.15s ease',
            }}
            title="Create New Custom Dashboard"
          >
            <FolderPlus size={14} /> + New Dashboard
          </button>
        </div>

        {/* Right: Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onRefresh}
            style={{
              padding: '7px 10px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              backgroundColor: '#ffffff',
              color: '#475569',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Refresh Data"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>

          <button
            type="button"
            onClick={onToggleEditMode}
            style={{
              padding: '7px 14px',
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
              backgroundColor: isEditMode ? '#f59e0b' : '#ffffff',
              color: isEditMode ? '#ffffff' : '#334155',
              border: isEditMode ? '1px solid #d97706' : '1px solid #cbd5e1',
            }}
          >
            <Sliders size={14} />
            {isEditMode ? 'Done Customizing' : 'Customize Layout (Drag & Drop)'}
          </button>

          <button
            type="button"
            onClick={onOpenAddWidget}
            className="btn-primary"
            style={{
              width: 'auto',
              padding: '7px 14px',
              fontSize: '12px',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Plus size={15} /> Add Widget
          </button>
        </div>
      </div>

      {/* Filter Bar: Global Filters */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          background: '#ffffff',
          padding: '10px 16px',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          fontSize: '12px',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px' }}>
          <span
            style={{
              fontWeight: 600,
              color: '#64748b',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <Filter size={13} color="#2563eb" /> Global Filters:
          </span>

          {/* Date Range Selector */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Calendar size={13} color="#94a3b8" />
            <select
              value={filters.dateRange || 'all'}
              onChange={(e) => onFilterChange({ ...filters, dateRange: e.target.value as any })}
              style={{
                padding: '4px 8px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                color: '#334155',
                fontSize: '12px',
                outline: 'none',
              }}
            >
              <option value="all">All Time</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="year">Current Year</option>
            </select>
          </div>

          {/* Severity Selector */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#64748b' }}>Severity:</span>
            <select
              value={filters.severity || 'ALL'}
              onChange={(e) => onFilterChange({ ...filters, severity: e.target.value })}
              style={{
                padding: '4px 8px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                color: '#334155',
                fontSize: '12px',
                outline: 'none',
              }}
            >
              <option value="ALL">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High / Major</option>
              <option value="NORMAL">Normal / Moderate</option>
              <option value="LOW">Low</option>
            </select>
          </div>

          {/* Department Selector */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: '#64748b' }}>Department:</span>
            <select
              value={filters.department || 'ALL'}
              onChange={(e) => onFilterChange({ ...filters, department: e.target.value })}
              style={{
                padding: '4px 8px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                color: '#334155',
                fontSize: '12px',
                outline: 'none',
              }}
            >
              <option value="ALL">All Departments</option>
              <option value="Flight Operations">Flight Operations</option>
              <option value="Ground Operations">Ground Operations</option>
              <option value="Cabin Crew">Cabin Crew Services</option>
              <option value="Maintenance">Technical & Maintenance</option>
              <option value="Safety & Quality">Safety & Quality Office</option>
            </select>
          </div>
        </div>

        {/* Clear Filters Button */}
        {isGlobalFiltered && (
          <button
            type="button"
            onClick={() => {
              onFilterChange({ dateRange: 'all', severity: 'ALL', department: 'ALL', crossFilters: undefined });
            }}
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#dc2626',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <X size={12} /> Reset Filters
          </button>
        )}
      </div>

      {/* Active Cross-Filter Chips Row */}
      {hasCrossFilters && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#f0f9ff',
            padding: '8px 14px',
            borderRadius: '8px',
            border: '1px solid #bae6fd',
            fontSize: '12px',
          }}
        >
          <span
            style={{
              fontWeight: 700,
              color: '#0369a1',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            <Layers size={13} color="#0284c7" /> Active Cross-Filters:
          </span>
          {Object.entries(filters.crossFilters!).map(([dim, val]) => (
            <span
              key={dim}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '3px 10px',
                borderRadius: '9999px',
                backgroundColor: '#ffffff',
                color: '#0369a1',
                border: '1px solid #93c5fd',
                fontWeight: 600,
                fontSize: '11px',
              }}
            >
              <span>{dim}:</span>
              <strong style={{ color: '#0f172a' }}>{val}</strong>
              <button
                type="button"
                onClick={() => removeCrossFilter(dim)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 0,
                }}
                title="Remove this filter"
              >
                <X size={12} />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={onClearCrossFilters}
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#0284c7',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              marginLeft: 'auto',
            }}
          >
            Clear Cross-Filters
          </button>
        </div>
      )}

      {/* New Dashboard Modal */}
      {isNewDashModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-card max-w-md">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FolderPlus size={18} color="#2563eb" /> Create New Analysis Dashboard
                </h3>
                <p>Define an isolated workspace for customized safety or quality metrics</p>
              </div>
              <button
                type="button"
                onClick={() => setIsNewDashModalOpen(false)}
                className="modal-close-btn"
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreateDashboardSubmit}>
              <div className="modal-body space-y-4" style={{ padding: '16px 20px' }}>
                <div className="form-field">
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                    Dashboard Title <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newDashTitle}
                    onChange={(e) => setNewDashTitle(e.target.value)}
                    placeholder="e.g. Fleet Hazards & Airworthiness Analytics"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '13px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      marginTop: '4px',
                    }}
                    required
                  />
                </div>
                <div className="form-field" style={{ marginTop: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                    Description (Optional)
                  </label>
                  <textarea
                    value={newDashDesc}
                    onChange={(e) => setNewDashDesc(e.target.value)}
                    placeholder="Scope, objectives, and monitored KPIs for this board..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      marginTop: '4px',
                    }}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '12px 20px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setIsNewDashModalOpen(false)}
                  className="btn-secondary"
                  style={{ width: 'auto', padding: '7px 14px', fontSize: '12px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ width: 'auto', padding: '7px 16px', fontSize: '12px' }}
                >
                  Create Dashboard
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
